import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Card, CardContent, CardActions, Button, Box, makeStyles, Typography, IconButton } from "@material-ui/core";
import { useTranslate } from "react-polyglot";
import CloseIcon from "@material-ui/icons/Close";
import PrimaryDarkTypography from "../../../SharedComponents/PrimaryDarkTypography.jsx";
import UniversalVideoPlayer from "../../../SharedComponents/UniversalVideoPlayer.jsx";
import useExtractRouteParamInt from "../../../hooks/useExtractRouteParamInt.js";
import { useNews } from "../../../Providers/Data/NewsProvider.jsx";
import { newsActionTypes } from "../../../constants.js";

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    display: "flex",
    flexDirection: "column",
    minHeight: 350,
    [theme.breakpoints.up(520)]: {
      flexDirection: "row",
    },
  },
  details: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflowY: "auto",
  },
  content: {
    flex: "1 0 auto",
    padding: "18px 24px",
    maxHeight: "70vh",
    overflowY: "auto",
  },
  contentBox: {
    "& p *": {
      overflowWrap: "anywhere",
    },
    overflowWrap: "anywhere",
    paddingBottom: "24px",
  },
  mediaBox: {
    width: ({ isImage }) => (isImage ? "100%" : 0),
    height: "auto",
    [theme.breakpoints.up(520)]: {
      maxWidth: ({ isImage }) => (isImage ? "40%" : 0),
    },
    margin: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px ${theme.spacing(1)}px`,
  },
  media: {
    display: "block",
    width: "100%",
    height: "auto",
    objectFit: "contain",
    borderRadius: 4,
  },
  action: {
    padding: "18px 24px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  videoPlayerWrapper: {
    width: 375,
    margin: "16px auto",
    "&>div": {
      width: "100%",
    },
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
}));

/**
 * @param {?TheNews} theNews
 * @param {?Function} onClose
 * @returns {*}
 * @constructor
 */
const NewsContent = ({ theNews, onClose }) => {
  const isImage = !!theNews?.image;
  const classes = useStyles({ isImage });
  const t = useTranslate();
  const history = useHistory();
  const { watchNews } = useNews();
  const newsId = useExtractRouteParamInt("/news/:newsId", "newsId");

  if (!theNews) {
    return null;
  }

  /** */
  const cardClickHandler = async () => {
    if (theNews.url) {
      await watchNews(theNews.id, newsActionTypes.buttonClick);
      window.open(theNews.url, "_blank").focus();
      if (newsId) {
        history.push("/news");
      }
    } else if (theNews.deck) {
      history.push(`/learn/${theNews.deck.id}`);
    }
    onClose && onClose();
  };

  /** */
  const onCloseCard = () => {
    if (newsId) {
      history.push("/news");
    }

    onClose && onClose();
  };

  return (
    <Card className={classes.cardRoot}>
      <Box className={classes.mediaBox}>
        <img className={classes.media} src={theNews.image?.url} />
      </Box>
      <div className={classes.details}>
        <CardContent className={classes.content}>
          {theNews.video?.url && (
            <Box className={classes.videoPlayerWrapper}>
              <UniversalVideoPlayer url={theNews.video.url} height={225} />
            </Box>
          )}
          <PrimaryDarkTypography variant="h6" gutterBottom>
            {theNews.name}
          </PrimaryDarkTypography>
          <Typography className={classes.contentBox} variant="body2">
            <span dangerouslySetInnerHTML={theNews.content.sanitizedData()} />
          </Typography>
        </CardContent>
        {(theNews.url || theNews.deck) && (
          <CardActions className={classes.action}>
            <Button onClick={cardClickHandler} variant="contained" color="primary">
              {theNews.buttonText || t("go")}
            </Button>
          </CardActions>
        )}
      </div>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onCloseCard}>
        <CloseIcon />
      </IconButton>
    </Card>
  );
};
NewsContent.propTypes = {
  theNews: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default NewsContent;
